import React from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useMotionPresets } from '../../theme/motion';

const resolveColor = (theme, color = 'primary.main') => {
  if (typeof color !== 'string' || !color.includes('.')) return color;
  const [key, shade] = color.split('.');
  return theme.palette?.[key]?.[shade] || theme.palette.primary.main;
};

export default function ActionTile({
  icon,
  title,
  description,
  meta,
  badge,
  color = 'primary.main',
  disabled = false,
  onClick,
  sx,
}) {
  const theme = useTheme();
  const motionPresets = useMotionPresets();
  const accent = resolveColor(theme, color);

  return (
    <Paper
      component={motion.button}
      type="button"
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      {...motionPresets.interactive}
      sx={[
        {
          width: '100%',
          height: '100%',
          p: { xs: 1.5, sm: 1.8 },
          borderRadius: theme.hr?.radius?.lg || 2,
          border: '1px solid',
          borderColor: alpha(accent, disabled ? 0.12 : 0.2),
          background:
            theme.palette.mode === 'dark'
              ? `linear-gradient(145deg, ${alpha(accent, 0.16)} 0%, ${alpha(theme.palette.background.paper, 0.8)} 64%)`
              : `linear-gradient(145deg, ${alpha(accent, 0.1)} 0%, ${alpha('#ffffff', 0.94)} 68%)`,
          color: 'text.primary',
          cursor: disabled ? 'not-allowed' : 'pointer',
          textAlign: 'left',
          font: 'inherit',
          boxShadow: theme.hr?.shadow?.soft,
          transition: theme.transitions.create(['border-color', 'box-shadow', 'transform'], {
            duration: theme.transitions.duration.short,
          }),
          '&:hover': disabled
            ? undefined
            : {
                borderColor: alpha(accent, 0.42),
                boxShadow: `0 18px 42px ${alpha(accent, 0.16)}`,
              },
          '&:disabled': {
            opacity: 0.64,
          },
        },
        sx,
      ]}
    >
      <Stack spacing={1.4} sx={{ minHeight: 132 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.2}>
          <Box
            sx={{
              width: 46,
              height: 46,
              borderRadius: theme.hr?.radius?.md || 2,
              display: 'grid',
              placeItems: 'center',
              color: accent,
              backgroundColor: alpha(accent, theme.palette.mode === 'dark' ? 0.2 : 0.12),
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          {badge ? (
            <Chip
              size="small"
              label={badge}
              sx={{
                fontWeight: 800,
                color: accent,
                backgroundColor: alpha(accent, theme.palette.mode === 'dark' ? 0.18 : 0.1),
              }}
            />
          ) : null}
        </Stack>

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 850, lineHeight: 1.18 }}>
            {title}
          </Typography>
          {description ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.65 }}>
              {description}
            </Typography>
          ) : null}
        </Box>

        {meta ? (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 'auto', fontWeight: 700 }}>
            {meta}
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
}
